/**
 * OrgDiamondV3 — 組織ダイヤモンド（4軸の組織平均）。
 *
 * 組織レポート v3（PC・モバイル）とホームの「組織の評価」カードで共有する。
 * 同じ数値を同じ形で出すための部品なので、見た目の調整はここ1箇所で行う。
 * 座標計算は個人レポートのダイヤモンドと共通（utils/diamondGeometry）。
 */
import React from 'react';
import type { AxisKey } from '../utils/gb74Report';
import {
  circlePath,
  polar,
  DIAMOND_AXIS_LABEL as AXIS_LABEL,
  DIAMOND_DEG as DEG,
  DIAMOND_ORDER as DIA_ORDER,
} from '../utils/diamondGeometry';

const BLUE = '#006ABD';

interface Props {
  /** 4軸の組織平均（0〜12・合計24） */
  axisMeans: Record<AxisKey, number>;
  /** 表示上の最大幅(px)。既定260 */
  maxWidth?: number;
}

export const OrgDiamondV3: React.FC<Props> = ({ axisMeans, maxWidth = 260 }) => {
  const VB = 260;
  const c = VB / 2;
  const R = 82;
  const at = (k: AxisKey, v: number) => polar(DEG[k], (Math.max(0, Math.min(12, v)) / 12) * R, c);
  const poly = DIA_ORDER.map((k) =>
    at(k, axisMeans[k])
      .map((n) => n.toFixed(1))
      .join(',')
  ).join(' ');
  return (
    <svg
      viewBox={`0 0 ${VB} ${VB}`}
      style={{ display: 'block', width: '100%', maxWidth, margin: '0 auto' }}
    >
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <path key={f} d={circlePath(c, c, R * f)} fill="none" stroke="#D8E4EF" />
      ))}
      {DIA_ORDER.map((k) => {
        const [x, y] = polar(DEG[k], R, c);
        return <line key={k} x1={c} y1={c} x2={x.toFixed(1)} y2={y.toFixed(1)} stroke="#D8E4EF" />;
      })}
      <polygon
        points={poly}
        fill="rgba(0,106,189,0.18)"
        stroke={BLUE}
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      {DIA_ORDER.map((k) => {
        const [x, y] = at(k, axisMeans[k]);
        return <path key={k} d={circlePath(x, y, 4)} fill={BLUE} />;
      })}
      {DIA_ORDER.map((k) => {
        const [x, y] = polar(DEG[k], R + 26, c);
        return (
          <text
            key={k}
            x={x.toFixed(1)}
            y={y.toFixed(1)}
            fill={BLUE}
            fontSize="11"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {AXIS_LABEL[k]} {axisMeans[k].toFixed(1)}
          </text>
        );
      })}
    </svg>
  );
};

export default OrgDiamondV3;
